import { useMutation, useQuery } from '@tanstack/react-query'
import type {
  RegistrationSubmissionSingleResponse,
  RegistrationSubmissionsListResponse
} from '@type/registrationForm.type'
import { AXIOS_INSTANCE } from '../api'
import { queryClient } from '../store/provider/TanStackProvider'

type ListProps = {
  page?: number
  limit?: number
  query?: string
  status?: string
  id?: string
}

export const useRegistrationSubmission = (arg: ListProps = {}) => {
  const { page = 1, limit = 20, query, status = 'pending', id } = arg

  const { data, isLoading } = useQuery<RegistrationSubmissionsListResponse>({
    enabled: !id,
    queryKey: ['registration_submissions', { page, limit, query, status }],
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<RegistrationSubmissionsListResponse>('/registration_submissions', {
        params: {
          'page[number]': page,
          'page[size]': limit,
          ...(query && { query }),
          ...(status && { status })
        }
      })
      return data
    }
  })

  const { data: singleSubmission, isLoading: singleSubmissionIsLoading } = useQuery<
    RegistrationSubmissionSingleResponse | undefined
  >({
    enabled: !!id,
    queryKey: ['registration_submissions', { id }],
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<RegistrationSubmissionSingleResponse>(
        `/registration_submissions/${id as string}`
      )
      return data
    }
  })

  const { mutateAsync: approveSubmission, isPending: approveIsLoading } = useMutation({
    mutationFn: async ({
      submissionId,
      force_create,
      charge_registration_fee
    }: {
      submissionId: string
      force_create?: boolean
      charge_registration_fee?: boolean
    }) => {
      const { data } = await AXIOS_INSTANCE.post(
        `/registration_submissions/${submissionId}/approve`,
        { force_create, charge_registration_fee },
        { headers: { 'Content-Type': 'application/json' } }
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['registration_submissions'] })
      queryClient.invalidateQueries({ queryKey: ['patients'] })
    }
  })

  const { mutateAsync: rejectSubmission, isPending: rejectIsLoading } = useMutation({
    mutationFn: async ({ submissionId, rejection_reason }: { submissionId: string; rejection_reason?: string }) => {
      const { data } = await AXIOS_INSTANCE.post(
        `/registration_submissions/${submissionId}/reject`,
        { rejection_reason },
        { headers: { 'Content-Type': 'application/json' } }
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['registration_submissions'] })
    }
  })

  return {
    data,
    pagination: data?.meta.pagination,
    isLoading,
    singleSubmission,
    singleSubmissionIsLoading,
    approveSubmission,
    approveIsLoading,
    rejectSubmission,
    rejectIsLoading
  }
}

/** Unauthenticated public registration form helpers */
export const fetchPublicRegistrationForm = async (shareToken: string) => {
  const { data } = await AXIOS_INSTANCE.get(`/public/registration_forms/${shareToken}`, {
    skipAuth: true,
    skipAuthHeaderPersist: true
  } as never)
  return data
}

export const submitPublicRegistrationForm = async (shareToken: string, answers: Record<string, unknown>) => {
  const { data } = await AXIOS_INSTANCE.post(`/public/registration_forms/${shareToken}/submissions`, { answers }, {
    headers: { 'Content-Type': 'application/json' },
    skipAuth: true,
    skipAuthHeaderPersist: true
  } as never)
  return data
}
